import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { Add, Delete, DragIndicator, ArrowUpward, ArrowDownward } from '@mui/icons-material';

const AcademicQuestionPaperForm = ({
  initialData = null,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    subject: '',
    standard: '',
    maximumMarks: '',
    academicYear: new Date().getFullYear().toString(),
    teacherId: '',
    teacherName: '',
    masterId: '',
    masterCode: '',
    createdBy: '',
    questionPapers: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        subject: initialData.subject || '',
        standard: initialData.standard || '',
        maximumMarks: initialData.maximumMarks || '',
        academicYear: initialData.academicYear || new Date().getFullYear().toString(),
        teacherId: initialData.teacherId || '',
        teacherName: initialData.teacherName || '',
        masterId: initialData.masterId || '',
        masterCode: initialData.masterCode || '',
        createdBy: initialData.createdBy || '',
        questionPapers: initialData.questionPapers || [],
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'isVerified' || name === 'isActive' ? checked : value,
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...formData.questionPapers];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      questionPapers: updatedQuestions,
    }));
  };

  const addQuestion = () => {
    setFormData((prev) => ({
      ...prev,
      questionPapers: [
        ...prev.questionPapers,
        {
          question: '',
          answer: '',
          maxMark: '',
          obtainedMark: '',
          questionNumber: prev.questionPapers.length + 1,
        },
      ],
    }));
  };

  const removeQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      questionPapers: prev.questionPapers.filter((_, i) => i !== index),
    }));
  };

  const moveQuestionUp = (index) => {
    if (index === 0) return;
    const updatedQuestions = [...formData.questionPapers];
    [updatedQuestions[index], updatedQuestions[index - 1]] = [updatedQuestions[index - 1], updatedQuestions[index]];
    // Update question numbers
    updatedQuestions.forEach((q, i) => q.questionNumber = i + 1);
    setFormData((prev) => ({
      ...prev,
      questionPapers: updatedQuestions,
    }));
  };

  const moveQuestionDown = (index) => {
    if (index === formData.questionPapers.length - 1) return;
    const updatedQuestions = [...formData.questionPapers];
    [updatedQuestions[index], updatedQuestions[index + 1]] = [updatedQuestions[index + 1], updatedQuestions[index]];
    // Update question numbers
    updatedQuestions.forEach((q, i) => q.questionNumber = i + 1);
    setFormData((prev) => ({
      ...prev,
      questionPapers: updatedQuestions,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.standard.trim()) {
      newErrors.standard = 'Standard is required';
    }
    if (!formData.academicYear.trim()) {
      newErrors.academicYear = 'Academic year is required';
    }

    formData.questionPapers.forEach((q, index) => {
      if (!q.question.trim()) {
        newErrors[`question_${index}`] = 'Question is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const submitData = {
        ...formData,
        maximumMarks: formData.maximumMarks ? Number(formData.maximumMarks) : null,
        questionPapers: formData.questionPapers.map(q => ({
          ...q,
          maxMark: q.maxMark ? Number(q.maxMark) : null,
          obtainedMark: q.obtainedMark ? Number(q.obtainedMark) : null,
        })),
      };
      onSubmit(submitData);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>
          Question Paper Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              error={!!errors.subject}
              helperText={errors.subject}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Standard"
              name="standard"
              value={formData.standard}
              onChange={handleChange}
              error={!!errors.standard}
              helperText={errors.standard}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Maximum Marks"
              name="maximumMarks"
              type="number"
              value={formData.maximumMarks}
              onChange={handleChange}
              error={!!errors.maximumMarks}
              helperText={errors.maximumMarks}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Academic Year"
              name="academicYear"
              value={formData.academicYear}
              onChange={handleChange}
              error={!!errors.academicYear}
              helperText={errors.academicYear}
              required
            />
          </Grid>

          {/* <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Teacher Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teacher ID"
                  name="teacherId"
                  value={formData.teacherId}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teacher Name"
                  name="teacherName"
                  value={formData.teacherName}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </Grid> */}

          {/* <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Master Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Master ID"
                  name="masterId"
                  value={formData.masterId}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Master Code"
                  name="masterCode"
                  value={formData.masterCode}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </Grid> */}

          {/* <Grid item xs={12}>
            <TextField
              fullWidth
              label="Created By"
              name="createdBy"
              value={formData.createdBy}
              onChange={handleChange}
            />
          </Grid> */}

          <Grid item xs={12}>
            <Box sx={{ mt: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Questions</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={addQuestion}
                  size="small"
                >
                  Add Question
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {formData.questionPapers.map((question, index) => (
                <Card key={index} variant="outlined" sx={{ position: 'relative' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => moveQuestionUp(index)}
                          disabled={index === 0}
                          sx={{ p: 0.5 }}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveQuestionDown(index)}
                          disabled={index === formData.questionPapers.length - 1}
                          sx={{ p: 0.5 }}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label={`Question ${index + 1}`}
                              value={question.question}
                              onChange={(e) =>
                                handleQuestionChange(index, 'question', e.target.value)
                              }
                              error={!!errors[`question_${index}`]}
                              helperText={errors[`question_${index}`]}
                              multiline
                              rows={2}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Answer"
                              value={question.answer}
                              onChange={(e) =>
                                handleQuestionChange(index, 'answer', e.target.value)
                              }
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Max Mark"
                              type="number"
                              value={question.maxMark}
                              onChange={(e) =>
                                handleQuestionChange(index, 'maxMark', e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Obtained Mark"
                              type="number"
                              value={question.obtainedMark}
                              onChange={(e) =>
                                handleQuestionChange(index, 'obtainedMark', e.target.value)
                              }
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => removeQuestion(index)}
                        sx={{ mt: 1 }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {formData.questionPapers.length === 0 && (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography color="text.secondary">
                  No questions added yet. Click "Add Question" to get started.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={onCancel} variant="outlined">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : initialData ? 'Update' : 'Create'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AcademicQuestionPaperForm;